/* eslint-disable max-lines-per-function */

import { useEffect, useMemo } from 'react';

import { PaymentsIntegrationConfiguration } from '@dreryk/egabinet-online-payments-integration-frontend-api';

import {
   ActionBar,
   Button,
   ButtonVariant,
   Heading,
   IconSize,
   IconWrapper,
   Link,
   LinkTarget,
   OpenInNewIcon,
   StyledFlex,
   Text,
   TextView,
} from '@dreryk/react-components';
import { createFileDescriptorFromBase64, createTranslatedText } from '@dreryk/react-common';

import { onlinePaymentsProviderDetailsTranslationKeys } from '../../translations';
import { OnlinePaymentsUpdateTermsOfServiceDocumentModalOpener } from '../integration/OnlinePaymentsUpdateTermsOfServiceDocumentModal.opener';

import { OnlinePaymentsIntegrationStatus } from './OnlinePaymentsIntegrationStatus';
import { OnlinePaymentsRemoveIntegrationModalOpener } from './OnlinePaymentsRemoveIntegrationModal.opener';

const TERMS_OF_SERVICE_FILE_PLACEHOLDER = '-';

type OnlinePaymentsProviderDetailsProps = {
   readonly onlinePaymentsIntegrationConfiguration: PaymentsIntegrationConfiguration;
};

export const OnlinePaymentsProviderDetails = (props: OnlinePaymentsProviderDetailsProps): JSX.Element => {
   const termsOfServiceUrl = useMemo<string | null>(() => {
      if (!props.onlinePaymentsIntegrationConfiguration.termsOfService) {
         return null;
      }

      const fileDescriptor = createFileDescriptorFromBase64(
         props.onlinePaymentsIntegrationConfiguration.termsOfService.data,
         props.onlinePaymentsIntegrationConfiguration.termsOfService.fileName,
         props.onlinePaymentsIntegrationConfiguration.termsOfService.mimeType
      );

      return URL.createObjectURL(fileDescriptor.file);
   }, [props.onlinePaymentsIntegrationConfiguration.termsOfService]);

   useEffect(() => {
      return () => {
         if (termsOfServiceUrl) {
            URL.revokeObjectURL(termsOfServiceUrl);
         }
      };
   }, [termsOfServiceUrl]);

   return (
      <ActionBar testId={'onlinePaymentsProviderDetails'} id={'onlinePaymentsProviderDetails'}>
         <StyledFlex direction={'column'} gap={40}>
            <section>
               <Heading
                  testId={'generalInformation'}
                  content={onlinePaymentsProviderDetailsTranslationKeys.generalInformation.header}
                  type={'h2'}
               />
               <StyledFlex direction={'column'} gap={24}>
                  <OnlinePaymentsIntegrationStatus />
               </StyledFlex>
            </section>

            <section>
               <Heading
                  testId={'integrationData'}
                  content={onlinePaymentsProviderDetailsTranslationKeys.integrationData.header}
                  type={'h2'}
               />
               <StyledFlex direction={'column'} gap={24}>
                  <TextView
                     testId={'posId'}
                     label={onlinePaymentsProviderDetailsTranslationKeys.integrationData.posId.label}
                  >
                     {props.onlinePaymentsIntegrationConfiguration.posId}
                  </TextView>
                  <TextView
                     testId={'secondKey'}
                     label={onlinePaymentsProviderDetailsTranslationKeys.integrationData.secondKey.label}
                  >
                     {props.onlinePaymentsIntegrationConfiguration.secondKey}
                  </TextView>
                  <TextView
                     testId={'clientId'}
                     label={onlinePaymentsProviderDetailsTranslationKeys.integrationData.clientId.label}
                  >
                     {props.onlinePaymentsIntegrationConfiguration.clientId}
                  </TextView>
                  <TextView
                     testId={'clientSecret'}
                     label={onlinePaymentsProviderDetailsTranslationKeys.integrationData.clientSecret.label}
                  >
                     {props.onlinePaymentsIntegrationConfiguration.clientSecret}
                  </TextView>
               </StyledFlex>
            </section>

            <section>
               <Heading
                  testId={'termsOfService'}
                  content={onlinePaymentsProviderDetailsTranslationKeys.termsOfService.header}
                  type={'h2'}
               />
               <StyledFlex direction={'column'} gap={'8px'}>
                  {termsOfServiceUrl ? (
                     <Link
                        href={termsOfServiceUrl}
                        target={LinkTarget.BLANK}
                        label={createTranslatedText(
                           props.onlinePaymentsIntegrationConfiguration.termsOfService!.fileName
                        )}
                        iconSuffix={
                           <IconWrapper testId={'openTermsOfServiceDocument'} size={IconSize.SMALL}>
                              <OpenInNewIcon />
                           </IconWrapper>
                        }
                        testId={'openTermsOfServiceDocument'}
                     />
                  ) : (
                     <Text testId={'termsOfServiceFilePlaceholder'}>{TERMS_OF_SERVICE_FILE_PLACEHOLDER}</Text>
                  )}
                  <OnlinePaymentsUpdateTermsOfServiceDocumentModalOpener>
                     {({ openModal }) => (
                        <Button
                           testId={'updateStatuteDocument'}
                           label={onlinePaymentsProviderDetailsTranslationKeys.actions.editTermsOfServiceFile}
                           onClick={openModal}
                           variant={ButtonVariant.SECONDARY}
                           blockingOperation
                        />
                     )}
                  </OnlinePaymentsUpdateTermsOfServiceDocumentModalOpener>
               </StyledFlex>
            </section>
         </StyledFlex>

         <OnlinePaymentsRemoveIntegrationModalOpener>
            {({ openModal }) => (
               <ActionBar.PrimaryButton
                  testId={'deleteIntegration'}
                  label={onlinePaymentsProviderDetailsTranslationKeys.actions.deleteIntegration}
                  onClick={openModal}
                  variant={ButtonVariant.PRIMARY}
                  blockingOperation
               />
            )}
         </OnlinePaymentsRemoveIntegrationModalOpener>
      </ActionBar>
   );
};
